import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import cx from 'classnames';
import { shape, func, string, bool } from 'prop-types';
import {
  Grid,
  withStyles,
  Typography,
  Tooltip,
  FormLabel,
} from '@material-ui/core';
import { get, lowerCase, startCase } from 'lodash';

import ApiKeys from './apiKeys';
import { getI18n } from '../../../../utils/intl';
import {
  Footer,
  FormActionButtons,
  DetailsPanelContainer,
  FormSelect,
} from '../../../../components';
import {
  fetchDeploymentRefreshTime,
} from '../../../../actions/portalConfig';
import {
  getDeploymentRefreshTime, getFeatureFlagForRemoteKeyStore,
} from '../../../../reducers/portalConfig';
import {
  fetchKeyStore,
  saveKeyStore,
  updateKeyStore,
  fetchProxyList,
  fetchKeyStoreKeyCounts,
  deployApiKeyToKeyStore,
} from '../../../../actions/keyStore';
import {
  getIsKeyStoreSaveSuccess,
  getKeyStoreDetails,
  getProxyListTotalElements,
  getKeyStoreDetailsKeyCounts,
  getIsKeyStoreBulkDeploymentSuccess,
} from '../../../../reducers/keyStore';
import {
  ALERT_SUCCESS,
  KEY_STORE_DEPLOYMENT_TYPE,
  KEY_STORE_TYPE_OTK,
  KEY_STORE_DISCONNECTED,
  KEY_STORE_TYPE,
  KEY_STORE_TYPE_REMOTE_KEY_STORE,
} from '../../../../constants';
import getDateTime from '../../../../utils/datetime';
import styles from './styles';
import FormTextField from "../../../../components/FormTextField";
import { hasError } from "../../../../utils";
import HelpIcon from "@material-ui/icons/Help";

export const Overview = (props) => {
  const {
    classes,
    isCurrentTab,
    keyStoreDetails,
    keyStoreDetailsKeyCounts,
    isKeyStoreSaveSuccess,
    isFieldEditName,
    notifyMessages,
    onHideEditTitle,
    uuid,
    proxyCount,
    keyStoreName,
    deploymentRefreshTime,
    featureFlagRemoteKeyStore,
    isKeyStoreBulkDeploymentSuccess,
  } = props;

  const intl = getI18n(useIntl());
  const [name, setName] = useState(keyStoreName);
  const [isFieldEdit, setIsFieldEdit] = useState(isFieldEditName);
  const [keyStoreType, setKeyStoreType] = useState(KEY_STORE_TYPE_OTK)
  const [remoteStoreId, setRemoteStoreId] = useState(null)
  const [deploymentType, setDeploymentType] = useState('AUTOMATIC')
  const [lastUpdated, setLastUpdated] = useState('');
  const [containerClass, setContainerClass] = useState(classes.paperKey);
  const [isRemoteKeyStoreType, setIsRemoteKeyStoreType] = useState(false);
  const [isRemoteStoreIdError, setIsRemoteStoreIdError] = useState(false);

  useEffect(() => {
    if(uuid) {
      props.fetchKeyStore(uuid);
      props.fetchKeyStoreKeyCounts(uuid);
      props.fetchProxyList({ uuid : uuid, hasAccess: 'ASSIGNED' });
      props.fetchDeploymentRefreshTime();
    }
  }, [uuid]);

  useEffect(() => {
    if(isKeyStoreBulkDeploymentSuccess) {
      props.fetchKeyStore(uuid);
      props.fetchKeyStoreKeyCounts(uuid);
    }
  }, [isKeyStoreBulkDeploymentSuccess]);

  useEffect(() => {
    if(keyStoreDetails) {
      setName(get(keyStoreDetails, 'name'))
      setRemoteStoreId(get(keyStoreDetails, 'remoteStoreId') || null);
      setDeploymentType(get(keyStoreDetails, 'deploymentType') || 'AUTOMATIC');
      setKeyStoreType(get(keyStoreDetails, 'keyStoreType') || KEY_STORE_TYPE_OTK);
      setIsRemoteKeyStoreType(get(keyStoreDetails, 'keyStoreType') === KEY_STORE_TYPE_REMOTE_KEY_STORE);
      const cClass = get(keyStoreDetails, 'connectionStatus') === KEY_STORE_DISCONNECTED ?
        cx(classes.paperKey, classes.disconnectedBackground) : classes.paperKey;
      setContainerClass(cClass)
    }
  }, [keyStoreDetails, isCurrentTab]);

  useEffect(() => {
    if(keyStoreDetailsKeyCounts) {
      setLastUpdated(get(keyStoreDetailsKeyCounts, 'lastDeploymentTs') || '');
    }
  }, [keyStoreDetailsKeyCounts]);

  useEffect(()=> {
    setIsFieldEdit(isFieldEditName);
  }, [isFieldEditName, isCurrentTab]);

  useEffect(()=> {
    setName(keyStoreName);
  }, [keyStoreName]);

  useEffect(() => {
    if (isKeyStoreSaveSuccess) {
      localStorage.getItem('page-key-store') === 'add' ?
        notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.key.store.created'))
      : notifyMessages(ALERT_SUCCESS, intl.getI18nMessage('label.key.store.updated'));
      props.push(`/admin/key-stores/details/${keyStoreDetails.uuid}`);
    }
  }, [isKeyStoreSaveSuccess, keyStoreDetails]);

  const handleAccess = (value) => {
    if(!isFieldEditName && value === get(keyStoreDetails, 'deploymentType')) {
      localStorage.setItem('isUnSavedChanges-key-store', false);
      setIsFieldEdit(false);
    } else {
      setIsFieldEdit(true);
      setDeploymentType(value);
      localStorage.setItem('isUnSavedChanges-key-store', true);
    }
  }

  const handleKeyStoreTypeChange = (value) => {
    setKeyStoreType(value);
    setIsRemoteKeyStoreType(value === KEY_STORE_TYPE_REMOTE_KEY_STORE);
    setIsFieldEdit(true);
    localStorage.setItem('isUnSavedChanges-key-store', true);
  }

  const handleRemoteStoreIdChange = (value) => {
    const remoteStoreIdUiError = hasError(value, false);
    setIsRemoteStoreIdError(remoteStoreIdUiError)
    setRemoteStoreId(value);
    setIsFieldEdit(true);
    localStorage.setItem('isUnSavedChanges-key-store', true);
  }

  const onDeployment = (isForceDeploy) => {
    props.deployApiKeyToKeyStore(isForceDeploy, uuid);
  }

  const onNext = () => {
    if(uuid) {
      localStorage.setItem('page-key-store', 'edit');
      props.updateKeyStore(name, deploymentType, uuid, keyStoreType, remoteStoreId);
    } else {
      localStorage.setItem('page-key-store', 'add');
      props.saveKeyStore(name, deploymentType, keyStoreType, remoteStoreId);
    }
    localStorage.setItem('isUnSavedChanges-key-store', false);
    setIsFieldEdit(false);
  }

  const onCancel = () => {
    localStorage.setItem('isUnSavedChanges-key-store', false);
    if(uuid) {
      onHideEditTitle();
      setIsFieldEdit(false);
    } else {
      props.push('/admin/key-stores');
    }
  }

  return (
    <div
      id="key-store-details-overview-container"
      data-apim-test="key-store-details-overview-container"
      data-layer7-test="key-store-details-overview-container"
    >
      <Grid container className={classes.marginPanelContainer}>
        <Grid item md={6} sm={12} xs={12} className={classes.paper}>
          <Grid item md={12} sm={12} xs={12} className={classes.topPanelContainer}>
            <DetailsPanelContainer
              conatinerClass={classes.panelContainer}
              id="key-store-overview-proxy"
              title={intl.getI18nMessage('label.key.store.details.proxies')}
              subTitle={!proxyCount ? intl.getI18nMessage('label.not.applicable') : proxyCount.toString()}
            />
          </Grid>
        </Grid>
        <Grid item md={6} sm={12} xs={12} className={containerClass}>
          <ApiKeys
            {...props}
            keyStoreName={name}
            onDeployment={onDeployment}
            refreshTime={deploymentRefreshTime}
            checkStatus={() => {
              props.fetchKeyStore(uuid);
              props.fetchKeyStoreKeyCounts(uuid);
            }}
          />
          {featureFlagRemoteKeyStore &&
            <div className={classes.keyStoreTypeContainer}>
            {uuid ?
              <div
                className={isRemoteKeyStoreType ? classes.keyStoreTypeDiv : ''}
                id={'key-store-overview-key-store-type'}
                data-apim-test={'key-store-overview-key-store-type'}
              >
                <Typography variant="h5" className={classes.paper}>
                  {intl.getI18nMessage('label.key.store.type')}
                </Typography>
                <Typography variant="body1">
                  {(keyStoreType === KEY_STORE_TYPE_OTK)
                    ? keyStoreType : startCase(lowerCase(keyStoreType))}
                </Typography>
              </div>
              :
              <FormSelect
                id={'key-store-overview-key-store-type'}
                fieldContainerClass={classes.keyStoreTypeDiv}
                name={intl.getI18nMessage('label.key.store.type')}
                value={keyStoreType}
                data={KEY_STORE_TYPE}
                handleChange={(value) => handleKeyStoreTypeChange(value)}
                noNativeSelect
                noNoneOption
              />
            }
            {isRemoteKeyStoreType && uuid &&
              <div
                className={classes.keyStoreIdDiv}
                id={'key-store-overview-remote-store-id'}
                data-apim-test={'key-store-overview-remote-store-id'}
              >
                <Typography variant="h5" className={classes.paper}>
                  {intl.getI18nMessage('label.key.store.remote.store.id')}
                </Typography>
                <Typography variant="body1">
                  {remoteStoreId}
                </Typography>
              </div>
            }
            {!uuid &&
              <div className={classes.keyStoreIdDiv}>
                <FormLabel className={classes.keyStoreIdLabel}>{intl.getI18nMessage('label.key.store.remote.store.id')}
                  <Tooltip className={classes.displayToolTip} title={intl.getI18nMessage("label.key.store.overview.remote.store.id.tooltip")} arrow placement="right">
                    <HelpIcon className={classes.displayToolTip} />
                  </Tooltip>
                </FormLabel>
                <FormTextField
                  id={'key-store-overview-remote-store-id'}
                  name={intl.getI18nMessage('label.key.store.remote.store.id')}
                  value={remoteStoreId}
                  maxLength={"255"}
                  disabled={!isRemoteKeyStoreType}
                  handleChange={(value) => handleRemoteStoreIdChange(value)}
                  error={isRemoteStoreIdError}
                  hideLabel={true}
                  labelClass={classes.keyStoreIdHiddenLabel}
               />
              </div>
            }
          </div>
          }
          <div className={classes.selectContainer}>
            <FormSelect
              id={'key-store-overview-deployment-type'}
              name={intl.getI18nMessage('label.key.store.key.deployment.type')}
              value={deploymentType}
              data={KEY_STORE_DEPLOYMENT_TYPE}
              handleChange={(value) => handleAccess(value)}
              noNativeSelect
              noNoneOption
            />
          </div>
          <div>
            {lastUpdated &&
              <div
                className={classes.selectContainer}
                id={'key-store-last-updated'}
                data-apim-test={'key-store-last-updated'}
              >
                <Typography variant="h5" className={classes.paper}>
                  {intl.getI18nMessage('label.last.updated')}
                </Typography>
                <Typography variant="body1">
                  {getDateTime(lastUpdated)}
                </Typography>
              </div>
            }
          </div>
        </Grid>
        <FormActionButtons
          isContainerFooter={true}
          onNextClick={onNext}
          onCancelClick={onCancel}
          nextText={intl.getI18nMessage('label.save.button')}
          cancelText={intl.getI18nMessage('label.cancel.button')}
          id="key-store-details-form-buttons"
          containerClass={!isFieldEdit ?
            classes.hideFooterContainer : classes.showFooterContainer}
        />
      </Grid>
      <Footer/>
    </div>
  );
};

Overview.propTypes = {
  classes: shape({}),
  keyStoreName: string,
  keyStoreDetails: shape({}),
  keyStoreDetailsKeyCounts: shape({}),
  isKeyStoreSaveSuccess: bool,
  uuid: string,
  isFieldEditName: bool,
  isCurrentTab: bool,
  notifyMessages: func,
  push: func,
  updateKeyStore: func,
  saveKeyStore: func,
  onHideEditTitle: func,
  proxyCount: string,
  fetchProxyList: func,
  fetchKeyStore: func,
  fetchKeyStoreKeyCounts: func,
  deployApiKeyToKeyStore: func,
  featureFlagRemoteKeyStore: bool,
  deploymentRefreshTime: func,
  fetchDeploymentRefreshTime: func,
  isKeyStoreBulkDeploymentSuccess: bool,
};

const mapStateToProps = state => ({
  isKeyStoreSaveSuccess: getIsKeyStoreSaveSuccess(state),
  keyStoreDetails: getKeyStoreDetails(state),
  proxyCount: getProxyListTotalElements(state),
  keyStoreDetailsKeyCounts: getKeyStoreDetailsKeyCounts(state),
  deploymentRefreshTime: getDeploymentRefreshTime(state),
  featureFlagRemoteKeyStore: getFeatureFlagForRemoteKeyStore(state),
  isKeyStoreBulkDeploymentSuccess: getIsKeyStoreBulkDeploymentSuccess(state),
});

const mapDispatchToProps = {
  saveKeyStore,
  updateKeyStore,
  fetchKeyStore,
  fetchProxyList,
  fetchKeyStoreKeyCounts,
  deployApiKeyToKeyStore,
  fetchDeploymentRefreshTime,
};

Overview.displayName = 'Overview';

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(Overview);

